//
// # Server Interface
//

// This file uses TypeScript declaration-merging pervasively: most exported
// names declare both a type and a value (a `{validator: ...}` companion or a
// re-export). The base eslint `no-redeclare` rule doesn't model that pattern,
// so it's disabled file-wide here.
/* eslint-disable no-redeclare */

import typia from "typia";
import {
  validate as v,
  Result as CoreResult,
  LoadKind,
  Context,
  ContextWire,
} from "CoreUtils";
import { schema } from "GenAlphaSchema";
import {
  type DbSpec,
  type Catalog as NoaCatalog,
  type Format,
  type ResolvedPath,
  type Schema,
  type ViewKind,
  type PDK as PDKType,
  SchematicValidator,
} from "./copies_of_types";

export type Error = { why: string; data?: unknown };
export type Result<T> = CoreResult<T, Error>;
export const Result = CoreResult;
export type AResult<T> = Promise<Result<T>>;

// re-export `Catalog` from `NoaSchema`
export type Catalog = NoaCatalog;
export const Catalog = {
  validator: new v.Validator(typia.createValidate<Catalog>()),
};

// re-export `PDK` from GenAlphaSchema v5
export type PDK = PDKType;
export const PDK = {
  validator: new v.Validator(typia.createValidate<PDK>()),
};

export type { DbSpec };

// re-export SchematicValidator
export { SchematicValidator };

export interface ServerClientInterface {
  // All methods require Context as first parameter for request tracing.

  // Save a supported `File`. Successful cases return an `Ok` result whose content is to be ignored (`unknown`).
  save(ctx: Context, file: Save): AResult<unknown>;

  // Open content from a supported `Open` object
  open(ctx: Context, arg: Open): AResult<Content>;

  // Get the catalog
  getCatalog(ctx: Context, db?: DbSpec): AResult<Catalog>;

  // Copilot prompt
  prompt(ctx: Context, prompt: Prompt): AResult<PromptResponseWire>; // FIXME: return type & validation

  getPdk(ctx: Context, pdkId: string): AResult<schema.pdk.PDK>;
  getStack(ctx: Context, pdkId: string, stackId: StackId): AResult<Stack>;

  // List available generators (optionally filtered by PDK)
  listGenerators(
    ctx: Context,
    pdkName?: string
  ): AResult<Array<GeneratorParamSpec>>;

  // List available PDKs
  listPdks(ctx: Context): AResult<string[]>;

  // List available symbols (generators + builtin) for symbol browser
  listAvailableSymbols(
    ctx: Context,
    pdkId?: string
  ): AResult<Array<SymbolInfo>>;

  // CRUD operations for instances

  // Create instance (parametric or non-parametric, with optional position)
  createInstance(
    ctx: Context,
    params: {
      designIdent: schema.Ident;
      symbolName: string;
      pdkId?: string;
      params?: Record<string, unknown>;
      position?: { x: number; y: number };
      name?: string; // Optional custom name, otherwise auto-generated
    }
  ): AResult<CreateInstanceResult>;

  // Read instance details
  getInstance(
    ctx: Context,
    params: {
      designIdent: schema.Ident;
      instanceId: string;
    }
  ): AResult<InstanceDetails>;

  // Update instance (name, params, position, etc.)
  updateInstance(
    ctx: Context,
    params: {
      designIdent: schema.Ident;
      instanceId: string;
      name?: string;
      params?: Record<string, unknown>;
      position?: { x: number; y: number };
    }
  ): AResult<unknown>;

  // Delete instance
  deleteInstance(
    ctx: Context,
    params: {
      designIdent: schema.Ident;
      instanceId: string;
    }
  ): AResult<unknown>;

  // Check if a design has unsaved changes
  isDirty(ctx: Context, ident: schema.Ident): AResult<boolean>;

  // Create a new layout from an existing schematic
  generateFromSource(
    ctx: Context,
    params: {
      schematicIdent: schema.Ident;
      pdkId: string;
      stackId: string;
    }
  ): AResult<schema.Ident>;
}

// Generator parameter specification
export interface GeneratorParamSpec {
  name: string;
  description: string;
  params: Array<{
    name: string;
    type: "number" | "string" | "boolean" | "enum";
    description: string;
    default?: unknown;
    constraints?: {
      min?: number;
      max?: number;
      enum?: Array<unknown>;
    };
  }>;
}

export const GeneratorParamSpec = {
  validator: new v.Validator(typia.createValidate<GeneratorParamSpec>()),
};

// Symbol information for symbol browser
export interface SymbolInfo {
  name: string;
  description: string;
  pdkId?: string; // PDK ID if PDK-specific, undefined for generic
  hasParams: boolean; // Whether symbol is parametric (backed by generator)
  generatorPath?: string[]; // Path to generator if parametric
  // Parameter spec, if parametric. NOT populated by `list-available-symbols` —
  // fetch it per symbol with `get-symbol-param-spec` (see that request's doc
  // for why). Still on the type because a caller that has fetched a spec
  // attaches it here for the dialog to consume.
  paramSpec?: GeneratorParamSpec;
}

export const SymbolInfo = {
  validator: new v.Validator(typia.createValidate<SymbolInfo>()),
};

// Instance details for editing
export interface InstanceDetails {
  instanceId: string;
  name: string;
  symbolName: string;
  position: { x: number; y: number };
  params?: Record<string, unknown>;
  paramSpec?: GeneratorParamSpec; // Parameter spec if parametric
  // Current segment arrangement of a layout instance: true when its segments
  // occupy more than one row. Undefined when the instance is not segmented, so
  // callers can tell "single row" from "no segments to speak of". Read off the
  // geometry, not stored — see inferRectangular.
  rectangular?: boolean;
}

export const InstanceDetails = {
  validator: new v.Validator(typia.createValidate<InstanceDetails>()),
};

// Non-fatal warning: some imported GDS geometry landed on (layer, datatype)
// pairs the PDK's layer map has no entry for.
export interface LayerImportWarning {
  unmappedElements: number;
  distinctLayers: number;
  unmappedSpecs: { layer: number; datatype: number; count: number }[];
}

// Result of creating an instance
export interface CreateInstanceResult {
  instanceId: string;
  instanceName: string;
  message: string;
  // The full Change that was applied server-side. Carried in the response so
  // the originating client can apply it locally and push it onto its undo
  // stack. Typed `unknown` here because the model-level Change types live in
  // GenAlphaModel (not the schema package); clients validate via
  // model.SchematicChange.validator / model.LayoutChange.validator.
  change: unknown;
  // Present only when this instance came from the external (Custom
  // Compiler) generator and its import had unmapped GDS layers.
  layerWarning?: LayerImportWarning;
}

export const CreateInstanceResult = {
  validator: new v.Validator(typia.createValidate<CreateInstanceResult>()),
};

export type StackId = schema.StackId;
export type Stack = schema.Stack;
export const Stack = {
  validator: new v.Validator(typia.createValidate<Stack>()),
};

// Image attachment for user-provided images in prompts
export type ImageAttachment = {
  imageKind: "png" | "jpeg" | "gif";
  encodingKind: "base64";
  imageEncoded: string;
};

export const ImageAttachment = {
  validator: new v.Validator(typia.createValidate<ImageAttachment>()),
};

export type Prompt = {
  prompt: string;
  // FIXME TEMPORARY: the active design's Ident, so the server can mark it
  // active for the agent. Move to proper UI-action piping when that exists.
  designIdent?: schema.Ident; // Optional to support no-design prompts
  requestId?: string;
  images?: ImageAttachment[]; // Optional user-attached images for multimodal prompts
  // Cap on output tokens (reasoning included) for the whole agent tree over the
  // conversation's life; the tree stops when crossed. Set by the eval harness.
  maxOutputTokens?: number;
};

// Wire view of `Conversation/src/response.ts` PromptResponse.
export interface PromptResponseWire {
  commentary: string;
  sections: Array<unknown>;
  systemCommentary: string;
}
export const PromptResponseWire = {
  validator: new v.Validator(typia.createValidate<PromptResponseWire>()),
};

export interface ConversationStatusWire {
  isBusy: boolean;
  // Output tokens spent by the agent tree so far (root + sub-agents, reasoning
  // included), the cap if a prompt set one, and whether the tree was stopped
  // for crossing it. Absent when the client has no conversation.
  outputTokens?: number;
  maxOutputTokens?: number;
  budgetExceeded?: boolean;
}
export const ConversationStatusWire = {
  validator: new v.Validator(typia.createValidate<ConversationStatusWire>()),
};

// Save request: provide either content directly, or sourceIdent to read from StateManager.
//
// `overwrite`: the user confirmed replacing an OpenAccess cellview that exists
// but was never read, after a save was refused as `oa-cellview-exists`. It
// never overrides a stale revision.
export type Save = (
  | { content: schema.Content; sourceIdent?: undefined; dest: Meta }
  | { content?: undefined; sourceIdent: schema.Ident; dest: Meta }
) & { overwrite?: boolean };

// Save Destination
export type Meta = DbMeta | IdentMeta | PathMeta;
export type DbMeta = { kind: "db"; db: DbSpec };
export type IdentMeta = { kind: "ident"; ident: schema.Ident };
export type PathMeta = {
  kind: "path";
  path: string | ResolvedPath;
  fmt: Format;
  schema: Schema;
  db?: DbSpec; // Optional database spec
};

export type Open = OpenPath | OpenIdent;

export type OpenPath = {
  kind: "path";
  path: string | ResolvedPath;
  fmt?: Format;
  schema?: Schema;
  db?: DbSpec;
  args?: {
    // Settings for how to find hierarchical dependencies when opening a path
    // "dir-db" means to use a directory as "database", and its naming convention for dependencies
    // "single-file" means to use a single file, i.e. only read the content of that file
    kind?: "single-file" | "dir-db"; // FIXME: dictates whether this uses "directory database"
  };
};

export type OpenIdent = {
  kind: "ident";
  ident: schema.Ident;
  db?: DbSpec; // Optional database spec for OpenAccess library/cell/view lookups
  // Optional PDK ID for layer mapping during OA imports.
  // If specified, the PDK will be loaded from depot and used to map
  // OA (layerNum, purposeNum) to GenAlpha layer/purpose numbers.
  pdkId?: string;
};

export type OpenResolvedPath = OpenPath & { path: ResolvedPath };

export type Content = schema.Content;
export const Content = {
  validator: new v.Validator(typia.createValidate<Content>()),
};

export type Ident = schema.Ident;
export const Ident = {
  validator: new v.Validator(typia.createValidate<Ident>()),
};

// Response type for open-hierarchy - returns all layouts in the hierarchy
export type OpenHierarchyResult = {
  // Array of all layouts in the hierarchy (root first, then children in BFS order)
  contents: Array<{ ident: schema.Ident; content: schema.Content }>;
  // Root layout ident for client to know which is the entry point
  root: schema.Ident;
};
export const OpenHierarchyResult = {
  validator: new v.Validator(typia.createValidate<OpenHierarchyResult>()),
};

// Response type for list-srv-rules — the read-only SRV rule descriptor list,
// sourced from the Rust ALL_RULES registry (matches GenAlphaModel's
// SrvRuleDescriptor structurally).
export type ListSrvRulesResult = Array<{
  id: string;
  label: string;
  description: string;
}>;
export const ListSrvRulesResult = {
  validator: new v.Validator(typia.createValidate<ListSrvRulesResult>()),
};

// # StopScope
// Scope of a `stop` request. "root" stops only the root agent (level 1,
// today's behavior); "tree" stops the root and every descendant (level 2).
export type StopScope = "root" | "tree";

// # Payload
// Discriminated union of all semantic operations a client can send to AlphaServer.
// Each variant pairs a string `kind` discriminator with a typed `data` payload.
// Construct values of this type directly at call sites; the client layer wraps
// them in an Envelope before serialization.

export type Payload =
  | {
      kind: "open";
      data: Open;
    }
  | {
      kind: "open-hierarchy";
      data: Open;
    }
  | {
      kind: "save";
      data: Save;
    }
  | { kind: "prompt"; data: Prompt }
  | {
      kind: "getcatalog";
      data?: DbSpec;
    }
  | {
      kind: "list-pdks";
      data?: undefined | null;
    }
  | {
      kind: "oa-tech"; // What technology an OpenAccess library resolves
      data: { lib: string };
    }
  | {
      kind: "list-oa-catalog";
      data?: undefined | null;
    }
  | {
      kind: "get-pdk";
      data: string; // PDK ID
    }
  | {
      kind: "get-stack";
      data: {
        pdkId: string;
        stackId: string;
      };
    }
  | {
      kind: "get-pdk-file"; // One of a PDK's typed sibling files, as its text
      data: {
        pdkId: string;
        name: string; // `cell-terminals.json`, `device-connectivity.json`, ...
      };
    }
  | {
      kind: "list-content";
      data?: undefined | null;
    }
  | {
      kind: "list-content-tree";
      data?: undefined | null;
    }
  | {
      kind: "content-exists-depot";
      data: { ident: schema.Ident };
    }
  | {
      kind: "list-stacks";
      data: string; // PDK ID
    }
  | {
      kind: "changes";
      data: {
        changes: Array<unknown>; // Really Array<model.Change>. FIXME #995 make them part of schema.
        source?: "gui" | "copilot" | "tool-server"; // Source of the changes
        clientId?: string; // Client ID for context tracking (used by gui/copilot)
        jobId?: string; // Job ID for async tool-server jobs (Rust generates this)
        ident?: schema.Ident; // Target design for multi-design clients
      };
    }
  | {
      kind: "list-generators";
      data?: string; // Optional PDK name filter
    }
  | {
      kind: "create-new-layout"; // Create a new empty layout
      data: {
        path: string[]; // Full design path (arbitrary depth); type is contentKind, not a path segment
        pdkId: string; // PDK identifier (required - layouts are PDK-specific)
        stackId: string; // Stack identifier (required - layouts need metal stack)
      };
    }
  | {
      kind: "create-new-schematic"; // Create a new empty schematic
      data: {
        path: string[]; // Full design path; type is its contentKind (see create-new-layout)
        pdkId?: string; // Optional PDK identifier for PDK-aware schematics
      };
    }
  | {
      kind: "create-new-symbol"; // Create a new empty symbol
      data: {
        path: string[]; // Full design path; type is its contentKind (see create-new-layout)
        pdkId?: string; // Optional PDK identifier for PDK-aware symbols
      };
    }
  | {
      kind: "is-dirty"; // Check if a design has unsaved changes
      data: schema.Ident; // Ident of the design to check
    }
  | {
      kind: "session-status"; // Does an orphaned (editor-less) unsaved in-memory session exist for this design?
      data: schema.Ident; // Ident of the design to check
    }
  | {
      kind: "discard-session"; // Drop the orphaned in-memory session so the next open reloads from depot
      data: schema.Ident; // Ident of the design whose session to discard
    }
  | {
      kind: "delete"; // Delete a design from the depot
      data: { ident: schema.Ident }; // Ident of the design to delete
    }
  | {
      kind: "generate-from-source"; // Create a new layout from an existing schematic
      data: {
        schematicIdent: schema.Ident; // Ident of the schematic to convert
        pdkId: string; // PDK identifier
        stackId: string; // Stack identifier
        layoutIdent: schema.Ident; // Ident for the new layout to create
        // For every m > 1 transistor: arrange its segments as a near-square
        // grid rather than the default single row. As on create-instance, a
        // placement input, not stored on any instance.
        rectangular?: boolean;
      };
    }
  | {
      kind: "register-client"; // Register a client with custom Copilot configuration
      data: {
        clientId: string; // Unique session identifier (per client instance)
        userId?: string; // Persistent user identity (hostname+username hash)
        config?: {
          model?: string; // Model ID (e.g., "anthropic/claude-opus-4.5"), defaults to tenancy config
          provider?: string[]; // Model providers (e.g., ["anthropic", "google-vertex"]), defaults to tenancy config
          temperature?: number; // Model temperature, default is 0.
          reasoningEffort?: string; // Reasoning effort level (e.g., "none", "low", "medium", "high", "xhigh")
        };
      };
    }
  | {
      kind: "close-client"; // Unregister a client and cleanup its resources
      data: {
        clientId: string; // Unique client identifier to cleanup
      };
    }
  | {
      kind: "get-trace"; // Get the trace for a given request
      data: {
        requestId: string; // Request ID
      };
    }
  | {
      kind: "list-available-symbols"; // List available symbols for symbol browser
      data?: // Optional PDK ID filter (string) or object with ident for correct content-kind lookup
      | string
        | {
            pdkId?: string;
            ident?: schema.Ident;
          };
    }
  | {
      // Parameter spec for ONE symbol. Split out of `list-available-symbols`,
      // which used to carry every symbol's full spec: external (Custom
      // Compiler) devices declare their real CDF params — up to 327 each —
      // which took that response from ~28KB to ~800KB on gf22. Callers need
      // one symbol's params at a time (the Add Instance form, once the user
      // has picked a device), so the list stays cheap and this fetches the
      // spec on demand. Resolves `pdkId`/`ident` exactly as
      // `list-available-symbols` does.
      kind: "get-symbol-param-spec";
      data: {
        symbolName: string;
        pdkId?: string;
        ident?: schema.Ident;
      };
    }
  | {
      kind: "create-instance"; // Create instance (CRUD: Create)
      data: {
        designIdent: schema.Ident;
        symbolName?: string;
        pdkId?: string;
        params?: Record<string, unknown>;
        position?: { x: number; y: number };
        name?: string;
        // When provided, use this ident directly as the instance target
        // instead of resolving via generator registry. Used for instancing
        // depot designs (layouts/symbols) from the DesignExplorer.
        targetIdent?: schema.Ident;
        // For m > 1: arrange the segments as a near-square grid rather than
        // the default single row. A placement input like `position`, not a
        // generator param — it is not stored on the instance.
        rectangular?: boolean;
      };
    }
  | {
      kind: "get-instance"; // Get instance details (CRUD: Read)
      data: {
        designIdent: schema.Ident;
        instanceId: string;
      };
    }
  | {
      kind: "update-instance"; // Update instance (CRUD: Update)
      data: {
        designIdent: schema.Ident;
        instanceId: string;
        name?: string;
        params?: Record<string, unknown>;
        position?: { x: number; y: number };
        // For m > 1: arrange the segments as a near-square grid rather than a
        // single row. Omitted keeps whatever shape the instance already has.
        rectangular?: boolean;
      };
    }
  | {
      kind: "delete-instance"; // Delete instance (CRUD: Delete)
      data: {
        designIdent: schema.Ident;
        instanceId: string;
      };
    }
  | {
      kind: "add-dummy-devices"; // Add dummy devices around instances
      data: {
        designIdent: schema.Ident;
        instanceIds: number[];
        nrows: number;
        ncols: number;
        dummyName: string;
      };
    }
  | {
      kind: "run-pex"; // Run parasitic extraction (PEX) on a layout via Rust tool server
      data: {
        designIdent: schema.Ident; // Ident of the layout design
      };
    }
  | {
      kind: "get-active-design-info"; // Get contentKind and pdkId of the active design
      data?: schema.Ident | undefined | null; // Optional ident to look up a specific design
    }
  | {
      kind: "capture-canvas"; // Capture the current canvas as a PNG image
      data?: undefined | null; // No data needed, captures the active webview
    }
  | {
      kind: "create-symbol-from-schematic"; // Generate a symbol from a schematic's pins
      data: {
        schematicIdent: schema.Ident; // Source schematic
        symbolIdent: schema.Ident; // Target symbol ident (caller provides)
        pdkId?: string; // PDK for the symbol; omit for pdk-agnostic
      };
    }
  | {
      kind: "ping"; // Or a ping, we wanna check if the server can respond
      data?: undefined | null; // Keep a dummy `data` so that all variants have one
    }
  | {
      kind: "link-design"; // Link two designs (bidirectional)
      data: {
        sourceIdent: schema.Ident; // The design being edited
        targetPath: string; // HierPath string of the target to link
      };
    }
  | {
      kind: "unlink-design"; // Unlink two designs (bidirectional)
      data: {
        sourceIdent: schema.Ident; // The design being edited
        targetIdent: schema.Ident; // The linked design to remove
      };
    }
  | {
      kind: "get-design-linkages"; // Get all linkages for a design
      data: {
        designIdent: schema.Ident; // The design to query
      };
    }
  | {
      kind: "run-lvs"; // Run LVS verification on a layout
      data: {
        designIdent: schema.Ident; // Ident of the layout design
        mode: "name_based" | "topology_based"; // Matching mode
      };
    }
  | {
      kind: "run-drc"; // Run DRC verification on a layout
      data: {
        designIdent: schema.Ident; // Ident of the layout design
      };
    }
  | {
      kind: "run-iva"; // Run IVA (Implementation vs Abstract) verification on a layout
      data: {
        designIdent: schema.Ident; // Ident of the layout design
      };
    }
  | {
      kind: "run-srv"; // Run SRV (Schematic Rules Validation) on a schematic
      data: {
        designIdent: schema.Ident; // Ident of the schematic design
      };
    }
  | {
      kind: "list-srv-rules"; // Fetch the read-only SRV rule descriptor list
      data: Record<string, never>; // no inputs — the list is fixed by the engine
    }
  | {
      kind: "infer-constraints"; // Infer symmetry constraints from schematic topology
      data: {
        designIdent: schema.Ident; // Ident of the schematic design
      };
    }
  | {
      kind: "place-with-symmetry"; // Place instances with symmetry constraints
      data: {
        designIdent: schema.Ident; // Ident of the layout design (must have linked schematic)
        constraintMatchIds?: number[]; // If provided, only place these specific constraint matches
      };
    }
  | {
      kind: "route-with-constraints"; // Route nets symmetrically based on constraints
      data: {
        designIdent: schema.Ident; // Ident of the layout design (must have linked schematic)
      };
    }
  | {
      kind: "mesh-power-router"; // Generate power distribution network using mesh topology
      data: {
        designIdent: schema.Ident; // Ident of the layout design
        config: {
          vddNet?: string;
          gndNet?: string;
          mesh?: {
            layerConfigs?: Array<{
              layer: number;
              name?: string;
              width: number;
              pitch: number;
              offset?: number;
            }>;
            alternating?: boolean;
          };
          ring?: { spacing: number; offset: number };
          // Note: meshLayers is deprecated - use mesh.layerConfigs instead
          meshLayers?: [number, number];
          // Track periods are used by frontend to calculate mesh pitch.
          // Passed here for logging/debugging; not directly used by backend.
          powerTrackPeriod?: number;
          groundTrackPeriod?: number;
        };
      };
    }
  | {
      kind: "depot-copy"; // Copy a design from depot to a new depot path
      data: {
        sourceIdent: schema.Ident; // Design to copy (hierpath, libcellview, or uuid)
        destPath: string[]; // Destination hierpath (e.g., ["_evals-temp", "test-123", "design"])
      };
    }
  | {
      kind: "rename"; // Rename a design (move its content to a new depot path)
      data: {
        sourceIdent: schema.Ident; // Design to rename
        destPath: string[]; // New full design path
      };
    }
  | {
      kind: "extract-schematic-netlist"; // Extract connectivity netlist from a schematic
      data: {
        schematicIdent: schema.Ident; // Ident of the schematic to extract netlist from
      };
    }
  | {
      kind: "stop"; // Stop in-flight agent work (two user-level stop requests)
      // scope "root" (default) stops only the root agent — the prior
      // cancel-prompt behavior. scope "tree" stops the root AND every
      // descendant. No unwinding:
      // in-flight LM/tool calls finish, then each agent ceases at its next
      // run-boundary checkpoint.
      data: { requestId?: string; scope?: StopScope };
    }
  | {
      kind: "conversation-status";
      data: Record<string, never>;
    }
  | {
      // Snapshot of the running agent hierarchy for this conversation, as a
      // nested AgentTreeNode (root → children). Routed by clientId like
      // conversation-status. Response: AgentTreeNode | null (null = no live
      // conversation/tree).
      kind: "get-agent-tree";
      data: Record<string, never>;
    }
  | {
      // Deliver a message directly to one agent in this conversation's tree
      // (registry-direct, scoped to the conversation root). Distinct from
      // `prompt`, which always goes to the root ide-user agent. If the target
      // is busy the message enqueues on its inbox and is processed after its
      // current turn. `agentId` must be the root or one of its descendants.
      kind: "message-agent";
      data: { agentId: string; prompt: string };
    }
  | {
      // Stop one agent AND every agent beneath it (cooperative halt), scoped to
      // this conversation's tree. Distinct from `stop`, which halts the whole
      // conversation by scope. `agentId` must be within this conversation's tree.
      kind: "stop-agent";
      data: { agentId: string };
    }
  | {
      // Retire (evict) a PARKED agent subtree — the target and its descendants
      // must all be inactive (stop them first). Refuses the conversation root.
      // Scoped to this conversation's tree.
      kind: "retire-agent";
      data: { agentId: string };
    }
  // ============================================================================
  // IDE Control (from WASM/tool code)
  // ============================================================================
  | {
      // Open a design in a new IDE tab. Sends a "design-open" WebSocket event.
      // Used by WASM code via ide_open_tab().
      kind: "ide-open-tab";
      data: { ident: schema.Ident; dirty?: boolean };
    }
  | {
      // Show a message in the IDE chat panel. Uses progress frame mechanism.
      // Used by WASM code via ide_show_message().
      kind: "ide-show-message";
      data: { message: string };
    };

// # Envelope
// Wire envelope shared by every Request. The client layer fills these fields
// before serialization; callers construct only a Payload.

export type Envelope = {
  clientId?: string; // Optional multi-client routing id
  context: ContextWire; // Required tracing context (logId, sessionId, tenant)
};

// # Request
// Full wire-shape: Envelope plus a Payload variant. This is what the server
// receives and validates; clients build it from Payload + Envelope at the
// HTTP boundary.

export type Request = Envelope & Payload;

export const Request = {
  validator: new v.Validator(typia.createValidate<Request>()),
};

// Issue Report
//
// Wire format for the in-IDE "Report an Issue" button (see issue #2146).
// The IDE posts this to AlphaServer's POST /report-issue endpoint. The server
// validates, rate-limits, and forwards to a sink (GitHub Issues + optional
// Slack webhook).
//
// Design *identifier* yes, design *content* no: `designIdent` mirrors the
// depot's `ident` shape so triage can look the entry up in the depot's
// index.json themselves. The schematic/layout JSON is never uploaded.

export type IssueReportCategory =
  | "crash"
  | "wrong-result"
  | "slow"
  | "ui"
  | "ai"
  | "other";

export interface IssueReport {
  // Client-supplied request context. Threaded through to the server log
  // so report-side log lines share logId / sessionId / tenant with the
  // IDE-side log entries (same pattern as ServerRequest.context). The
  // /report-issue route sits behind JWT auth, so the IDE always has a
  // populated Context to supply — no need for a server-side background
  // fallback.
  context: ContextWire;

  // Primary — required for any correlation
  sessionId: string; // IDE session
  timestamp: string; // ISO 8601

  // The actual report
  description: string; // required, free text
  category?: IssueReportCategory; // optional, no default

  // Secondary — fine to include, low cost
  user: { email: string; tenant: string };
  designIdent?: {
    // design *identifier* only, not contents. Mirrors the depot's `ident`
    // shape so triage can look up the entry in index.json directly.
    kind: "hierpath";
    path: string[]; // e.g. ["sandia_lib", "vco_test_ring_3stg", "schematic"]
    uuid?: string; // depot uuid if known
  };
  pdk?: { id: string; stackId?: string };
  screenshotPngBase64?: string; // optional, size-capped client-side

  // Build identity, useful for triage
  env?: string; // alpha-server | dev | ...
  commitHash?: string;
}

export const IssueReport = {
  validator: new v.Validator(typia.createValidate<IssueReport>()),
};

// Response: where the report ended up. `trackingUrl` is optional — v1
// has no GitHub Issues sink, so a successful report just acks with an
// empty object. When a future sink (GitHub App, ticket system, etc.)
// lands, populate this with the resulting URL.
export interface IssueReportResponse {
  trackingUrl?: string;
}

export const IssueReportResponse = {
  validator: new v.Validator(typia.createValidate<IssueReportResponse>()),
};

// --- Layout generation diagnostics (generate-from-source) -----------------
//
// When a schematic can't be fully converted to a layout, the server reports
// the blocking problems per-instance instead of silently producing an empty
// layout. Shared here so the Design (server) side and the IDE (client) side
// agree on the shape; the IDE renders these in the Console panel.

// Why a single schematic instance can't be turned into layout.
//   - "generator-not-found": no PDK generator/builtin backs this device
//     (e.g. a `res` builtin in a PDK that has no `res` generator).
//   - "param-validation": the device generator exists but the instance's
//     parameters violate its spec (e.g. nmos `nser` below the minimum).
//   - "unresolved-hierarchy": the instance points at a user cell (symbol)
//     that has no PDK-compatible layout to place hierarchically.
//   - "unknown-target": the instance target isn't a shape we know how to
//     dispatch to a generator or sub-layout at all.
export type GenerationIssueKind =
  | "generator-not-found"
  | "param-validation"
  | "unresolved-hierarchy"
  | "unknown-target";

// One blocking problem, tied to the schematic instance that caused it.
export interface GenerationIssue {
  instanceName: string; // Schematic instance name (e.g. "M_400pF")
  target: string; // Stringified target ident (for display)
  kind: GenerationIssueKind;
  message: string; // Human-readable, ready to show in the console
  details?: string[]; // Extra lines (e.g. individual validation errors)
}

// The full diagnostic for one generate-from-source attempt.
export interface GenerationReport {
  blocked: boolean; // True when issues prevented (or would prevent) a layout
  issues: GenerationIssue[];
  instancesTotal: number;
  instancesConverted: number; // 0 when blocked pre-conversion
}

export const GenerationReport = {
  validator: new v.Validator(typia.createValidate<GenerationReport>()),
};

// Error payload returned by generate-from-source when no usable layout was
// produced. `why` distinguishes a pre-conversion block ("preflight-failed")
// from a post-conversion shortfall ("generation-incomplete"); both carry the
// same report so the client renders them identically.
//
// The report rides in `data`: the IDE client's request layer only preserves
// the `why` and `data` fields of a server error (see AlphaClient.call), so a
// sibling field would be dropped in transit.
export interface GenerateFromSourceError {
  why: "preflight-failed" | "generation-incomplete";
  data: GenerationReport;
}

export const GenerateFromSourceError = {
  validator: new v.Validator(typia.createValidate<GenerateFromSourceError>()),
};

// WebSocket Message Types for Design Operations between clients and AlphaServer

// Base message interface
export interface BaseWebSocketMessage {
  context: ContextWire; // Request context for tracing (Go-style context threading)
  id?: string; // For request correlation
  clientId?: string; // For multi-client support
  timestamp?: string;
}

// === Messages from Client to AlphaServer ===

export interface AddDesignMessage extends BaseWebSocketMessage {
  kind: "add-design";
  ident: schema.Ident;
  content: schema.Content;
  viewKind?: ViewKind;
}

export interface RemoveDesignMessage extends BaseWebSocketMessage {
  kind: "remove-design";
  ident: schema.Ident;
}

export interface ListDesignsMessage extends BaseWebSocketMessage {
  kind: "list-designs";
}

// Capture canvas response - sent from client to AlphaServer
// after capturing the active webview's canvas
export interface CaptureCanvasResponseMessage extends BaseWebSocketMessage {
  kind: "capture-canvas-response";
  uuid: string;
  success: boolean;
  imageBase64?: string;
  error?: string;
}

// Union type for all incoming design messages from client to AlphaServer
export type DesignRequestMessage =
  | AddDesignMessage
  | RemoveDesignMessage
  | ListDesignsMessage
  | CaptureCanvasResponseMessage;

// === Messages from AlphaServer to Client ===

export interface DesignOperationResponse extends BaseWebSocketMessage {
  kind: "design-operation-response";
  requestId?: string; // Correlates with request ID
  success: boolean;
  error?: {
    why: string;
    details?: unknown;
  };
  data?: unknown; // Response data for queries
}

export interface DesignAddedEvent extends BaseWebSocketMessage {
  kind: "design-added";
  ident: schema.Ident;
}

export interface DesignRemovedEvent extends BaseWebSocketMessage {
  kind: "design-removed";
  ident: schema.Ident;
}

// Design load event - server specifies how client should load content
// loadKind="refresh": preserve UI state (zoom/pan), update content only
// loadKind="new": reset UI state (fit to content), load fresh
export interface DesignLoadEvent extends BaseWebSocketMessage {
  kind: "design-load";
  ident: schema.Ident;
  contentKind: string; // "layout-tree" or "schematic"
  loadKind: LoadKind; // Server specifies load behavior
  savedIdents?: schema.Ident[]; // Sub-designs whose change triggered this refresh; the client invalidates just their cached content. One at a time today; an array so coalesced refreshes can name several.
}

// Incremental design-change event - server streams the changes applied to a
// design so the client applies them live to its canvas. `changes` is typed
// `Array<unknown>` because ServerInterface doesn't depend on GenAlphaModel (it
// is `model.Change[]`; the client validates + applies via
// `applyServerOriginatedChange`).
export interface DesignChangesEvent extends BaseWebSocketMessage {
  kind: "changes";
  ident: schema.Ident;
  contentKind: schema.ContentKind;
  changes: Array<unknown>; // internally model.Change[]
  // The shared-state version this change set advances the design TO. Monotonic
  // per design; the client can compare against the last version it applied to
  // gap-detect a dropped/out-of-order frame and recover via a design-load
  // refresh.
  serverVersion: number;
}

// Design saved event - notifies client that a design was saved
// Used to trigger tree view refresh when saving to a new filename
export interface DesignSavedEvent extends BaseWebSocketMessage {
  kind: "design-saved";
  ident: schema.Ident;
  identChanged: boolean; // True if this was a "Save As" to a new filename
}

// Design open event - tells the IDE to open a new tab for a design.
// Sent when the LM opens a design (e.g. depot open tool).
export interface DesignOpenEvent extends BaseWebSocketMessage {
  kind: "design-open";
  ident: schema.Ident;
  dirty?: boolean;
}

// LVS result event - sent when LVS verification completes.
// The IDE uses this to populate the LVS panel with errors/results.
export interface LvsResultEvent extends BaseWebSocketMessage {
  kind: "lvs-result";
  ident: schema.Ident;
  result: {
    passed?: boolean;
    errors?: Array<{
      kind: string;
      message: string;
      net?: string;
      instance?: string;
      pin?: string;
      details?: unknown;
    }>;
    stats?: {
      schematicNetCount?: number;
      layoutConnCount?: number;
      schematicInstanceCount?: number;
      layoutInstanceCount?: number;
      matchedNetCount?: number;
      openCount?: number;
      shortCount?: number;
      missingInstanceCount?: number;
      extraInstanceCount?: number;
      missingPinCount?: number;
      extraPinCount?: number;
      comparisonTimeUs?: number;
    };
  };
}

// DRC result event - sent when DRC verification completes.
// The IDE uses this to populate the DRC panel with errors/results.
export interface DrcResultEvent extends BaseWebSocketMessage {
  kind: "drc-result";
  ident: schema.Ident;
  result: {
    passed?: boolean;
    errors?: Array<{
      kind: string;
      ruleId: string;
      message: string;
      severity: "error" | "warning" | "info";
      instances?: string[];
      bbox?: {
        min: { x: number; y: number };
        max: { x: number; y: number };
      };
      details?: unknown;
    }>;
    stats?: {
      instancesChecked: number;
      rulesChecked: number;
      checkTimeUs: number;
      overlappingPairs: number;
      skipped?: { rule: string; reason: string }[];
      notes?: string[];
    };
  };
}

// IVA result event - sent when IVA verification completes.
// The IDE uses this to populate the IVA panel with errors/results.
export interface IvaResultEvent extends BaseWebSocketMessage {
  kind: "iva-result";
  ident: schema.Ident;
  result: {
    passed: boolean;
    errors: Array<{
      kind: string;
      message: string;
      instance?: string;
      pin?: string;
      layer?: number;
      bbox?: {
        min: { x: number; y: number };
        max: { x: number; y: number };
      };
      point?: { x: number; y: number };
      details?: unknown;
    }>;
    stats: {
      instanceCount: number;
      routeSegmentCount: number;
      instanceBlockageCount: number;
      pinCount: number;
      errorCount: number;
      checkTimeUs: number;
    };
  };
}

// SRV result event - sent when Schematic Rules Validation completes.
// The IDE uses this to populate the SRV panel with violations/results.
export interface SrvResultEvent extends BaseWebSocketMessage {
  kind: "srv-result";
  ident: schema.Ident;
  result: {
    ranRuleIds: string[];
    violations: Array<{
      ruleId: string;
      message: string;
      points: Array<{ x: number; y: number }>;
      wireSegmentIds: number[];
      schPinIds: number[];
      instanceIds: number[];
    }>;
    stats: Array<{
      ruleId: string;
      violationCount: number;
      elapsedMs: number;
    }>;
  };
}

// Union type for all WebSocket design events from AlphaServer to client
export type DesignWebSocketEvent =
  | DesignOperationResponse
  | DesignAddedEvent
  | DesignRemovedEvent
  | DesignLoadEvent
  | DesignChangesEvent
  | DesignSavedEvent
  | DesignOpenEvent
  | LvsResultEvent
  | DrcResultEvent
  | IvaResultEvent
  | SrvResultEvent;

// Distributive Omit for union types - applies Omit to each member of the
// union. `T extends unknown ? ... : never` is the canonical distributive
// pattern; `unknown` here behaves the same as `any` for distribution but
// satisfies `no-explicit-any`.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

// Event body type without context - for functions that add context automatically
export type DesignWebSocketEventBody = DistributiveOmit<
  DesignWebSocketEvent,
  "context"
>;

//
// # Subscriber — server-side post-apply event fanout
//
// A `Subscriber` is the server-internal handle a party registers with
// `StateService` to receive design-state events. Each subscriber
// translates internal events into whatever shape it cares about — today
// AlphaServer's `Client` implements one that emits the matching wire
// events (`design-load`, `lvs-result`, etc.) to its WebSocket.
//
// Subscribers are identified by `clientId`. The broker's optional
// `excludeOriginClient` filter (GUI optimistic-UI escape hatch on
// `/changes`) keys off it. Lifecycle: register per (design, subscriber)
// via `StateService.subscribe`; the returned `Unsubscribe` removes it.
//
// `onChange` is the only required callback; the others are optional
// per-event. A subscriber registers for events it cares about by
// implementing the matching callback and leaves the rest undefined.
//
export interface Subscriber {
  // Identifies the originating party — used by `broadcast` for
  // origin-exclusion when callers pass `excludeOriginClient`.
  clientId: string;

  // Design state changed (incremental apply path). `serverVersion` is the
  // version this change set advances the design to, so the receiver can stamp
  // the wire event for gap-detection (empty `changes` ⇒ a full-reload refresh).
  // `changedSubDesigns` is set only on a parent-design refresh (empty changes):
  // it names the children whose edits triggered this refresh, so the receiver
  // can invalidate just those sub-designs' cached content instead of everything.
  // One entry today; an array so coalesced refreshes can carry several.
  onChange: (
    ctx: Context,
    designIdent: schema.Ident,
    changes: unknown[],
    serverVersion: number,
    changedSubDesigns?: schema.Ident[]
  ) => void;

  // Wholesale state replacement (vs. incremental change). Today's wire
  // shape: `design-load` with `loadKind:"new"`.
  onStateReplaced?: (
    ctx: Context,
    designIdent: schema.Ident,
    contentKind: string
  ) => void;

  // Per-turn progress messages from LM tools.
  onProgress?: (ctx: Context, requestId: string, message: string) => void;

  // Verification / validation results. Result payload shapes are
  // referenced via indexed access on the matching wire event type so
  // there's one canonical definition.
  onLvsResult?: (
    ctx: Context,
    designIdent: schema.Ident,
    result: LvsResultEvent["result"]
  ) => void;
  onDrcResult?: (
    ctx: Context,
    designIdent: schema.Ident,
    result: DrcResultEvent["result"]
  ) => void;
  onIvaResult?: (
    ctx: Context,
    designIdent: schema.Ident,
    result: IvaResultEvent["result"]
  ) => void;
  onSrvResult?: (
    ctx: Context,
    designIdent: schema.Ident,
    result: SrvResultEvent["result"]
  ) => void;

  // Design saved to depot. `identChanged` is true for save-as.
  onDesignSaved?: (
    ctx: Context,
    designIdent: schema.Ident,
    identChanged: boolean
  ) => void;

  // LM-initiated design open — IDE opens a new tab.
  onDesignOpen?: (
    ctx: Context,
    designIdent: schema.Ident,
    dirty: boolean
  ) => void;
}

// Returned by `StateService.subscribe`; invoking it removes the
// subscription. No-op if already unsubscribed.
export type Unsubscribe = () => void;

// === Progress Event (server to client) ===
// Sent during copilot operations to report progress. The IDE routes each frame
// to the matching conversation's transcript by `conversationId`: it
// holds one persistent WS subscription and dispatches by conversationId, so
// `requestId` is not carried (it was only ever used for routing).
export interface ProgressEvent {
  type: "progress";
  conversationId: string;
  message: string;
  timestamp: string;
}

// === Capture Canvas Request (server to client) ===
// Sent to request a screenshot of the active canvas
export interface CaptureCanvasRequestEvent {
  kind: "capture-canvas-request";
  uuid: string;
}

// Union type for all server-to-client WebSocket events
export type ServerWebSocketEvent =
  | DesignWebSocketEvent
  | ProgressEvent
  | CaptureCanvasRequestEvent;

// Validators for WebSocket message types
export const DesignRequestMessage = {
  validator: new v.Validator(typia.createValidate<DesignRequestMessage>()),
};

export const DesignWebSocketEvent = {
  validator: new v.Validator(typia.createValidate<DesignWebSocketEvent>()),
};

export const ProgressEvent = {
  validator: new v.Validator(typia.createValidate<ProgressEvent>()),
};

export const CaptureCanvasRequestEvent = {
  validator: new v.Validator(typia.createValidate<CaptureCanvasRequestEvent>()),
};

export const ServerWebSocketEvent = {
  validator: new v.Validator(typia.createValidate<ServerWebSocketEvent>()),
};
